import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import GlossarySearch from "../components/GlossarySearch";
import LessonModulesGrid from "../components/LessonModulesGrid";
import { BookOpen, TrendingUp, BarChart3, Activity, AlertTriangle, Sparkles, MousePointer2, ArrowRight } from "lucide-react";

const LESSONS = [
  {
    icon: BarChart3,
    color: "#547A8F",
    title: "What is a candlestick?",
    body: "A candlestick shows four prices for a time period: the open (where price started), close (where it ended), and the high & low it reached. A green candle means the close was higher than the open. Red means the close was lower. Beginners often start with simpler line or area charts before moving to candlesticks.",
  },
  {
    icon: TrendingUp,
    color: "#5C8D73",
    title: "Trends, support & resistance",
    body: "A trend is the general direction price is moving — up, down, or sideways. Support is a price level where buyers tend to step in (price often bounces up). Resistance is where sellers step in (price often gets pushed down). These aren't magic lines — they're just historical zones of activity.",
  },
  {
    icon: Activity,
    color: "#D18E63",
    title: "Reading volume",
    body: "Volume = how many shares or coins traded in a period. High volume during a price move suggests strong interest and conviction. Low volume on a big move can mean it's less reliable. Always look at price + volume together for context.",
  },
  {
    icon: AlertTriangle,
    color: "#C66B59",
    title: "Avoiding emotional trading",
    body: "FOMO (fear of missing out) and panic selling are the biggest enemies of new traders. The market will always offer another opportunity. Set rules ahead of time, stick to them, and never invest more than you can afford to lose. Past performance never guarantees future results.",
  },
  {
    icon: Sparkles,
    color: "#547A8F",
    title: "How AI can help (and can't)",
    body: "UpTick's AI explains charts in plain English so you can learn faster. But AI doesn't predict the future — it summarizes what already happened in friendly language. Treat AI insights as a study aid, not a signal to trade.",
  },
];

export default function Learn() {
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-8 lg:px-12 py-10 md:py-14" data-testid="learn-page">
        <div className="mb-12 animate-fade-up">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#EAE6E1] bg-white text-xs mb-4">
            <BookOpen className="w-3.5 h-3.5 text-[#547A8F]" strokeWidth={1.5} />
            <span className="text-[#6B7068] uppercase tracking-[0.2em] font-semibold">
              Learn hub
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-[#2A2F2B]">
            Trading basics, <span className="italic text-[#547A8F]">demystified</span>.
          </h1>
          <p className="text-lg text-[#6B7068] mt-4 max-w-2xl">
            Bite-sized lessons written for total beginners. No textbook tone. Just clarity.
          </p>
        </div>

        <div className="mb-12">
          <LessonModulesGrid />
        </div>

        <Link
          to="/sandbox"
          data-testid="learn-sandbox-cta"
          className="block mb-12 rounded-2xl bg-gradient-to-br from-[#547A8F]/8 to-[#D18E63]/8 dark:from-[#6E94A8]/15 dark:to-[#DDA284]/15 border border-[#547A8F]/20 dark:border-[#6E94A8]/30 p-6 md:p-8 transition-soft hover:-translate-y-0.5 hover:shadow-md group"
        >
          <div className="flex items-start gap-5">
            <div className="w-12 h-12 rounded-xl bg-[#547A8F]/15 dark:bg-[#6E94A8]/20 flex items-center justify-center shrink-0">
              <MousePointer2 className="w-6 h-6 text-[#547A8F] dark:text-[#87A8BA]" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#547A8F] dark:text-[#87A8BA]">
                Practice
              </div>
              <h2 className="font-heading text-2xl text-[#2A2F2B] dark:text-[#E8E6E2] mt-0.5">
                Try the Chart Sandbox
              </h2>
              <p className="text-sm text-[#6B7068] mt-1 max-w-lg">
                Click to plot your own support/resistance lines. Toggle moving averages to see golden + death crosses. Risk-free, no API limits, infinite reshuffles.
              </p>
            </div>
            <ArrowRight className="w-5 h-5 text-[#6B7068] group-hover:text-[#547A8F] dark:group-hover:text-[#87A8BA] group-hover:translate-x-1 transition-soft shrink-0 mt-2" />
          </div>
        </Link>

        <div className="space-y-5">
          {LESSONS.map((l, i) => (
            <article
              key={l.title}
              data-testid={`lesson-${i}`}
              className="rounded-2xl bg-white border border-[#EAE6E1] p-7 transition-soft hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(42,47,43,0.05)] animate-fade-up"
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${l.color}15` }}
                >
                  <l.icon className="w-5 h-5" style={{ color: l.color }} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl tracking-tight text-[#2A2F2B] mb-2">
                    {l.title}
                  </h2>
                  <p className="text-[#2A2F2B] leading-relaxed">{l.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-12">
          <GlossarySearch />
        </div>

        <div className="mt-12 rounded-2xl bg-[#547A8F]/5 border border-[#547A8F]/20 p-6 text-center">
          <p className="text-sm text-[#2A2F2B]">
            UpTick is for educational use only. Nothing here is financial advice.
            Always do your own research.
          </p>
        </div>
      </main>
    </div>
  );
}
